/**
 * Advanced Image Modal Component with Gallery Navigation
 * 
 * A feature-rich modal component for displaying images with full-screen viewing,
 * touch/swipe gestures, keyboard navigation, and image gallery functionality.
 * 
 * Key Features:
 * - Full-screen image display with responsive sizing
 * - Touch/swipe gestures for mobile navigation
 * - Keyboard navigation (arrows, escape)
 * - Image preloading and caching for performance
 * - Automatic orientation detection (portrait/landscape)
 * - Smooth transitions between images
 * - Loading states and error handling
 * - Body scroll prevention while modal is open
 * 
 * Navigation Controls:
 * - Touch: Swipe left/right on mobile devices
 * - Keyboard: Arrow keys for prev/next, Escape to close
 * - Click: Navigation arrows when showNavigation is true
 * - Click outside: Closes modal
 * 
 * Performance Optimizations:
 * - Image preloading for smooth transitions
 * - Debounced touch events to prevent multiple triggers
 * - Cached image dimensions to avoid layout shifts
 * - Transition states to manage smooth animations
 * 
 * @param {string} image - URL of the image to display
 * @param {string} alt - Alt text for accessibility
 * @param {function} onClose - Callback when modal should close
 * @param {function} onPrev - Callback for previous image navigation
 * @param {function} onNext - Callback for next image navigation
 * @param {boolean} showNavigation - Whether to show navigation arrows
 * 
 * @description Full-featured image modal with touch gestures and keyboard navigation
 */

import { useEffect, useState, useRef } from 'react';
import Image from 'next/image';

const ImageModal = ({ image, alt, onClose, onPrev, onNext, showNavigation = false }) => {
  const [imageOrientation, setImageOrientation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState({});
  const [transitioning, setTransitioning] = useState(false);
  const modalRef = useRef(null);
  const containerRef = useRef(null);
  
  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;
  
  // Add keyboard event listener for Escape key and arrow navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && showNavigation && onPrev && !transitioning) {
        handleNavigation('prev');
      } else if (e.key === 'ArrowRight' && showNavigation && onNext && !transitioning) {
        handleNavigation('next');
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    // Prevent scrolling while modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, onPrev, onNext, image, showNavigation, transitioning]);

  // Load and cache the current image when it changes
  useEffect(() => {
    if (!image) return;
    
    // Reset loading state for the new image
    setIsLoading(true);
    setTransitioning(true);
    
    // Check if this image is already preloaded
    if (preloadedImages[image]) {
      setImageOrientation(preloadedImages[image].orientation);
      setIsLoading(false);
      
      // Short delay to allow transition to complete
      const transitionTimer = setTimeout(() => {
        setTransitioning(false);
      }, 300); // Match transition-duration in the CSS
      
      return () => clearTimeout(transitionTimer);
    }
    
    // Otherwise load the image
    const img = new window.Image();
    img.onload = () => {
      const orientation = img.height > img.width ? 'portrait' : 'landscape';
      
      // Cache the image dimensions and orientation
      setPreloadedImages(prev => ({
        ...prev,
        [image]: { 
          width: img.width, 
          height: img.height,
          orientation: orientation,
          aspectRatio: img.width / img.height
        }
      }));
      
      setImageOrientation(orientation);
      setIsLoading(false);
      
      // Short delay to allow transition to complete
      const transitionTimer = setTimeout(() => {
        setTransitioning(false);
      }, 300); // Match transition-duration in the CSS
    };
    
    img.src = image;
    
    return () => {
      img.onload = null;
    };
  }, [image, preloadedImages]);
  
  // Preload adjacent images for smoother navigation
  useEffect(() => {
    if (!showNavigation) return;
    
    // Function to preload a single image
    const preloadImage = (imageToPreload) => {
      if (!imageToPreload || preloadedImages[imageToPreload]) return;
      
      const img = new window.Image();
      img.onload = () => {
        const orientation = img.height > img.width ? 'portrait' : 'landscape';
        setPreloadedImages(prev => ({
          ...prev,
          [imageToPreload]: { 
            width: img.width, 
            height: img.height,
            orientation: orientation,
            aspectRatio: img.width / img.height
          }
        }));
      };
      img.src = imageToPreload;
    };
    
    // Create a custom event to request preloading of next/previous images
    const requestPreload = (e) => {
      if (e.detail?.nextImage) {
        preloadImage(e.detail.nextImage);
      }
      if (e.detail?.prevImage) {
        preloadImage(e.detail.prevImage);
      }
    };
    
    window.addEventListener('requestImagePreload', requestPreload);
    
    return () => {
      window.removeEventListener('requestImagePreload', requestPreload);
    };
  }, [showNavigation, preloadedImages]);

  // Wrapper for navigation that adds transition state
  const handleNavigation = (direction) => {
    if (transitioning) return;
    
    setTransitioning(true);
    
    if (direction === 'prev') {
      onPrev();
    } else {
      onNext();
    }
  };

  // Handle navigation arrow clicks with event propagation stopped
  const handlePrev = (e) => {
    e.stopPropagation();
    if (onPrev && !transitioning) {
      handleNavigation('prev');
    }
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (onNext && !transitioning) {
      handleNavigation('next');
    }
  };
  
  // Touch event handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(true);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    setIsSwiping(false);
    if (!touchStart || !touchEnd || transitioning) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && onNext) {
      handleNavigation('next');
    } else if (isRightSwipe && onPrev) {
      handleNavigation('prev');
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity" 
      onClick={onClose}
      ref={modalRef}
    >
      {isLoading && !preloadedImages[image] ? (
        // Loading state - centered spinner
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
        </div>
      ) : (
        // Main container with navigation and image
        <div className="flex items-center justify-center w-full">
          {/* Left/Previous Arrow - Only visible on non-mobile */}
          {showNavigation && (
            <button 
              className={`hidden md:flex absolute left-4 lg:left-8 z-10 bg-black/50 hover:bg-black/70 rounded-full p-3 text-white transition-all duration-300 focus:outline-none ${transitioning ? 'opacity-50 cursor-not-allowed' : 'opacity-100'}`}
              onClick={handlePrev}
              aria-label="Previous image"
              disabled={transitioning}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          
          {/* Image content container */}
          <div 
            ref={containerRef}
            className={`relative max-h-[85vh] mx-auto transition-all duration-300 ${
              imageOrientation === 'portrait' 
                ? 'max-w-[65vh] w-auto' 
                : 'max-w-5xl w-[90%]'
            }`}
          >
            {/* Close button */}
            <button 
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 focus:outline-none"
              onClick={onClose}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image container with touch event handlers */}
            <div 
              className={`relative h-auto rounded-lg overflow-hidden bg-black shadow-xl max-h-[85vh] ${
                imageOrientation === 'portrait' ? 'w-auto' : 'w-full'
              }`}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {/* Mobile swipe indicators - only visible on touch devices when navigation is enabled */}
              {showNavigation && (
                <>
                  <div className={`md:hidden absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 rounded-full p-2 transition-opacity duration-300 ${isSwiping ? 'opacity-70' : 'opacity-30'}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </div>
                  <div className={`md:hidden absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 rounded-full p-2 transition-opacity duration-300 ${isSwiping ? 'opacity-70' : 'opacity-30'}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </>
              )}
              
              <div className="relative w-full h-full">
                <Image
                  src={image}
                  alt={alt || "Enlarged view"}
                  className={`object-contain max-h-[85vh] transition-opacity duration-300 ${transitioning ? 'opacity-50' : 'opacity-100'}`}
                  width={1200}
                  height={1800}
                  priority={true}
                  style={{ 
                    width: imageOrientation === 'portrait' ? 'auto' : '100%', 
                    height: 'auto',
                    maxHeight: '85vh',
                  }}
                />
              </div>
            </div>
          </div>
          
          {/* Right/Next Arrow - Only visible on non-mobile */}
          {showNavigation && (
            <button 
              className={`hidden md:flex absolute right-4 lg:right-8 z-10 bg-black/50 hover:bg-black/70 rounded-full p-3 text-white transition-all duration-300 focus:outline-none ${transitioning ? 'opacity-50 cursor-not-allowed' : 'opacity-100'}`}
              onClick={handleNext}
              aria-label="Next image"
              disabled={transitioning}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageModal; 